// ─────────────────────────────────────────────────────────────────────────────
// Painel de gerência — a busca dos dados.
//
// O painel inteiro sai de DUAS consultas, e isso é uma decisão de projeto, não
// um acaso:
//
//   1. `POST /dashboard/suporte/ticket/pendentes` devolve a fila INTEIRA do
//      setor, com o dono (`codigoOperador`) em cada linha. Uma resposta cobre
//      carga, agendamentos, fila sem dono, encalhados e criticidade — cinco dos
//      seis blocos. A alternativa seria `/tickets/filtro` uma vez por operador
//      por status: treze pessoas × cinco status = 65 chamadas a cada ciclo;
//   2. um SELECT em `orion.operador_atividade` traz os períodos em aberto, que
//      é o estado de cada um AGORA.
//
// Quem transforma isso em quadro é services/painelGerencia.js, que não conhece
// rede. Aqui só se busca e se normaliza.
// ─────────────────────────────────────────────────────────────────────────────

import { MYSUITE_API_BASE } from '../config/ambiente';
import { supabase, tabela, tabelaOperador } from './supabase';
import { dataLocalISO, horaParaMinutos } from './datas';
import { SETOR_SUPORTE } from './permissoes';
import { STATUS_SO_NO_FILTRO, normalizarTicket, unirFilas, indicePorDescricao } from './painelGerencia';

/**
 * A equipe do plantão: quem é do suporte, está ativo e TEM jornada cadastrada.
 *
 * A jornada é o filtro que separa quem está sob controle de atividade de quem
 * não está — sem ela o Orion não abre período nenhum (ver `carregarExpediente`
 * em atividadeApi.js), e a pessoa apareceria eternamente como "sem registro
 * hoje". Diretoria e coordenação, que de propósito não têm jornada, ficam fora
 * do quadro em vez de figurarem como ausentes todo dia.
 */
export async function carregarEquipe() {
  try {
    const { data, error } = await (await tabelaOperador())
      .select('ope_codigo, ope_nome, ope_sobrenome, ope_email, ope_setor, '
        + 'ope_jornada_ini, ope_jornada_fim, ope_almoco_ini, ope_almoco_fim, '
        + 'ope_tolerancia_min, ope_cafe_limite_min')
      .eq('ope_enabled', true)
      .not('ope_jornada_ini', 'is', null)
      .order('ope_nome');

    if (error || !Array.isArray(data)) {
      console.warn('[Orion] Equipe do painel:', error?.message || 'resposta inesperada');
      return [];
    }

    return data
      // O filtro de setor é feito aqui, e não no `.eq()`, para aceitar as
      // variações de caixa com que 'SUP' aparece no cadastro.
      .filter(o => String(o.ope_setor || '').trim().toUpperCase() === SETOR_SUPORTE)
      .map(o => ({
        codigo: Number(o.ope_codigo),
        nome: `${o.ope_nome || ''} ${o.ope_sobrenome || ''}`.trim(),
        email: String(o.ope_email || '').trim().toLowerCase(),
        expediente: {
          ini: horaParaMinutos(o.ope_jornada_ini),
          fim: horaParaMinutos(o.ope_jornada_fim),
          almocoIni: horaParaMinutos(o.ope_almoco_ini),
          almocoFim: horaParaMinutos(o.ope_almoco_fim),
          tolerancia: Number.isFinite(o.ope_tolerancia_min) ? o.ope_tolerancia_min : 5,
          cafeLimite: Number.isFinite(o.ope_cafe_limite_min) ? o.ope_cafe_limite_min : 15,
        },
      }));
  } catch (err) {
    console.warn('[Orion] Equipe do painel:', err.message);
    return [];
  }
}

/**
 * `{ [ope_codigo]: nome }` de TODO operador cadastrado — sem filtro nenhum.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * POR QUE ESTA CONSULTA É SEPARADA DE `carregarEquipe`
 *
 * São perguntas diferentes, e tratá-las como uma só foi o defeito:
 *
 *   · `carregarEquipe` responde "quem está de PLANTÃO?" — e por isso filtra
 *     por SUP, ativo e com jornada;
 *   · esta responde "quem é o operador 104?", e não pode filtrar NADA. O
 *     dono de um ticket na fila do suporte não é necessariamente alguém do
 *     plantão de hoje.
 *
 * Medido na fila real em 31/08/2026: dos 16 códigos com ticket, três ficavam
 * de fora da equipe e apareciam como "Operador 104" — Gabriel Medeiros e
 * Jonathan (setor SIS) e Júlio César (sem jornada cadastrada). Nenhum deles
 * era dado ausente: os três estavam em `orion.operador` o tempo todo.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ONDE ENTRA O space.usuarios
 *
 * Aqui dentro, sem que este código precise saber. `tabelaOperador()` lê de
 * `orion.vw_operador`, e é a VIEW que faz o join com `space.usuarios`: onde
 * há vínculo (`usr_codigo`), o `ope_nome` que chega já é o nome do cadastro
 * corporativo; sem vínculo, é o do Orion. Ver
 * sql/view-operador_orion-fonte-space_*.sql.
 *
 * E é o único caminho possível: a chave anon NÃO alcança o schema `space`
 * (`42501: permission denied`), e a view atravessa porque roda com os
 * privilégios do dono. Consultar `space.usuarios` direto do app não
 * funcionaria — e nem resolveria, porque lá a chave é `usr_codigo`, e não o
 * código do MySuite que a fila devolve. Quem liga os dois é a view.
 *
 * Falhar aqui não quebra a tela: sem o mapa, o painel volta a mostrar
 * "Operador 104", que é feio mas não é errado.
 */
export async function carregarNomes() {
  try {
    const { data, error } = await (await tabelaOperador())
      .select('ope_codigo, ope_nome, ope_sobrenome');

    if (error || !Array.isArray(data)) {
      console.warn('[Orion] Nomes dos operadores:', error?.message || 'resposta inesperada');
      return {};
    }

    const mapa = {};
    data.forEach((o) => {
      const codigo = Number(o.ope_codigo);
      if (!Number.isFinite(codigo)) return;
      const nome = `${o.ope_nome || ''} ${o.ope_sobrenome || ''}`.trim();
      // Cadastro sem nome preenchido não vira string vazia no mapa: o painel
      // mostraria uma lacuna, e "Operador 104" informa mais do que nada.
      if (nome) mapa[codigo] = nome;
    });
    return mapa;
  } catch (err) {
    console.warn('[Orion] Nomes dos operadores:', err.message);
    return {};
  }
}

/**
 * Os períodos de atividade EM ABERTO e quem já tem registro hoje.
 *
 * As duas informações saem da mesma tabela e são pedidas juntas porque
 * respondem à mesma pergunta em duas metades: o período aberto diz o que a
 * pessoa está fazendo; a presença de qualquer linha no dia diz se ela chegou a
 * abrir o Orion. Sem a segunda, quem encerrou o expediente e quem nunca entrou
 * apareceriam idênticos.
 *
 * Devolve `null` em caso de falha — e não listas vazias. São coisas diferentes:
 * vazio é "ninguém está em pausa", null é "não sei", e tratar um como o outro
 * pintaria o time inteiro de ausente por causa de uma queda de rede.
 */
export async function carregarAtividadeDoDia(agora = new Date()) {
  const hoje = dataLocalISO(agora);
  try {
    const [resAbertos, resHoje] = await Promise.all([
      tabela('operador_atividade')
        .select('id, operador_codigo, operador_nome, status, tipo, origem, inicio, apoio_operador_nome, observacao')
        .is('fim', null),
      tabela('operador_atividade')
        .select('operador_codigo')
        .eq('data_ref', hoje),
    ]);

    if (resAbertos.error || resHoje.error) {
      console.warn('[Orion] Atividade do painel:',
        resAbertos.error?.message || resHoje.error?.message);
      return null;
    }

    const periodos = (resAbertos.data || []).map(p => ({
      ...p,
      operador_codigo: Number(p.operador_codigo),
      // A tela compara com `agora`; converter aqui evita que cada uso refaça a
      // leitura do timestamptz.
      inicio: new Date(p.inicio),
    }));

    return {
      periodos,
      comRegistroHoje: [...new Set((resHoje.data || []).map(r => Number(r.operador_codigo)))],
    };
  } catch (err) {
    console.warn('[Orion] Atividade do painel:', err.message);
    return null;
  }
}

/**
 * A fila de pendentes do suporte, inteira.
 *
 * Devolve `null` quando falha, pelo mesmo motivo de sempre: um painel que zera
 * por causa de rede faria o operador acreditar que a fila esvaziou.
 */
export async function carregarFilaDoSuporte() {
  try {
    const res = await fetch(`${MYSUITE_API_BASE}/dashboard/suporte/ticket/pendentes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!res.ok) throw new Error(`O MySuite respondeu ${res.status}`);
    const tickets = await res.json();
    return Array.isArray(tickets) ? tickets : null;
  } catch (err) {
    console.warn('[Orion] Fila do suporte:', err.message);
    return null;
  }
}

/**
 * A fila GERAL de um status, por `POST /tickets/filtro` SEM operador.
 *
 * Descoberto conferindo a API em 31/08/2026: omitindo `codigoOperador`, o
 * endpoint devolve o status inteiro do setor, e não a fatia de uma pessoa. É o
 * único caminho para o status 18 (Setor de Sistemas), que a fila de pendentes
 * não devolve — e que sozinho tinha 81 tickets naquele dia, o dobro da fila de
 * pendentes inteira.
 *
 * A resposta vem em OUTRO formato (ver `normalizarTicket`): `operador` no lugar
 * de `codigoOperador`, status e criticidade como texto, e sem `dataAbertura`.
 * Quem normaliza é o módulo de regras; aqui só se busca.
 */
export async function carregarFilaPorStatus(codigoStatus) {
  try {
    const res = await fetch(`${MYSUITE_API_BASE}/tickets/filtro`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ codigoStatus: Number(codigoStatus) }),
    });
    if (!res.ok) throw new Error(`O MySuite respondeu ${res.status}`);
    const dados = await res.json();
    const lista = Array.isArray(dados) ? dados : (dados?.tickets || dados?.dados || []);
    return Array.isArray(lista) ? lista : null;
  } catch (err) {
    console.warn(`[Orion] Fila geral do status ${codigoStatus}:`, err.message);
    return null;
  }
}

/**
 * As descrições de criticidade, para o painel não mostrar código cru.
 *
 * Consulta própria, e não `carregarListas()`, porque aquela função busca também
 * ocorrências, classificações e os relacionamentos entre elas — quatro tabelas
 * das quais o painel usa uma. Falhar aqui não é problema: sem o mapa, o bloco
 * mostra "Criticidade 4" em vez do texto, e o resto da tela segue igual.
 */
export async function carregarCriticidades() {
  try {
    const { data, error } = await supabase
      .from('criticidade')
      .select('cri_codigo, cri_descricao')
      .eq('cri_ativo', 1);
    if (error || !Array.isArray(data)) return {};
    const mapa = {};
    data.forEach((c) => { mapa[Number(c.cri_codigo)] = c.cri_descricao; });
    return mapa;
  } catch (err) {
    return {};
  }
}

/**
 * Tudo de uma vez, em paralelo.
 *
 * As consultas são independentes, então esperar uma pela outra só somaria
 * latência. Cada uma trata o próprio erro: o painel abre com o que chegou, e a
 * tela avisa o que faltou — meia tela é melhor do que nenhuma quando alguém
 * quer saber o que está estourando prazo.
 *
 * `comEquipe` desliga as duas consultas do quadro de equipe (cadastro e
 * atividade) para quem não é admin. Não é economia: é que o painel de um
 * operador comum NÃO DEVE nem buscar o estado de atividade dos colegas. O que
 * não é lido não pode vazar por um erro de renderização mais tarde.
 */
export async function carregarPainel(agora = new Date(), { comEquipe = false } = {}) {
  const [equipe, atividade, nomes, pendentes, criticidades, ...porStatus] = await Promise.all([
    comEquipe ? carregarEquipe() : Promise.resolve([]),
    comEquipe ? carregarAtividadeDoDia(agora) : Promise.resolve({ periodos: [], comRegistroHoje: [] }),
    // Os nomes valem para TODO MUNDO, gerente ou não: quem lê a fila precisa
    // saber com quem está o ticket para não ligar ao mesmo cliente duas vezes.
    carregarNomes(),
    carregarFilaDoSuporte(),
    carregarCriticidades(),
    // As filas que só existem em /tickets/filtro — hoje, o Setor de Sistemas.
    ...STATUS_SO_NO_FILTRO.map(s => carregarFilaPorStatus(s)),
  ]);

  const porDescricao = indicePorDescricao(criticidades);

  // A fila de pendentes vem PRIMEIRO na união: quando o mesmo ticket aparece
  // nas duas origens, o registro dela vence, por ser o mais completo (traz
  // dataAbertura e os códigos crus).
  const normalizados = [
    ...(pendentes || []).map(t => normalizarTicket(t, { porDescricao })),
    ...STATUS_SO_NO_FILTRO.flatMap((status, i) => (porStatus[i] || [])
      .map(t => normalizarTicket(t, { statusPedido: status, porDescricao }))),
  ].filter(Boolean);

  const tickets = unirFilas(normalizados);

  // Fila é null só quando NENHUMA origem respondeu. Se uma delas veio, o painel
  // mostra o que tem — e o aviso de falha parcial explica o resto.
  const nenhumaOrigem = pendentes === null && porStatus.every(r => r === null);

  return {
    equipe,
    nomes,
    periodos: atividade?.periodos || [],
    comRegistroHoje: atividade?.comRegistroHoje || [],
    tickets: nenhumaOrigem ? null : tickets,
    criticidades,
    // O que NÃO respondeu. A tela usa para explicar uma leitura incompleta em
    // vez de mostrar zeros que parecem informação.
    falhas: {
      equipe: comEquipe && !equipe.length,
      atividade: comEquipe && atividade === null,
      fila: pendentes === null,
      filasExtras: porStatus.some(r => r === null),
      nomes: !Object.keys(nomes).length,
    },
  };
}
